<template>
  <Content
    v-loading="loading"
    class="col-span-full"
    :title="$t($route.name) + ' - ' + $t('QUALITY_TITLE') + ': ' + qualityTitle"
    :router="{
      name: 'REPORT_OVERVIEW_v3',
    }"
  >
    <div
      v-if="!!result"
      class="
        flex
        lg:gap-12
        gap-6
        flex-col
        lg:flex-row
        items-start
        justify-start
      "
    >
      <Field :title="$t('VEHICLE_TITLE')" type="col" align="start">
        <LicensePlate
          type="dashboard"
          :vehicle="result.company_vehicle.vehicle"
          :color="result.company_vehicle.color"
          :license_plate="result.company_vehicle.license_plate"
          :chars="alphaList"
        ></LicensePlate>
      </Field>
      <Field
        v-if="result.company_vehicle.driver"
        :title="$t('DRIVER_TITLE')"
        type="col"
        align="start"
      >
        <p>
          {{
            result.company_vehicle.driver.first_name +
            ' ' +
            result.company_vehicle.driver.last_name
          }}
        </p>
      </Field>

      <Field
        :title="$t('DURATION') + ' (' + $t('HOUR') + ')'"
        type="col"
        align="start"
      >
        <span>
          {{ calculateHour(result.route.duration) }}
        </span>
      </Field>

      <Field :title="$t('ODOMETER')" type="col" align="start">
        <span
          v-html="$options.filters.numberSeperator1(result.route.length / 1000)"
        ></span>
      </Field>
    </div>

    <overview-detail-map v-if="!!result" :route="result.route" />

    <Content
      class="col-span-full mt-8"
      childClass="border-whitee"
      title="SPEED_GRAPH"
      :showBreadcrumb="false"
      :showAdd="false"
      v-if="!!result"
    >
      <VueApexCharts
        type="line"
        height="350"
        :options="{
          chartOptions,
          labels: date,
          annotations: {
            yaxis: [
              {
                y: result.route.max_speed,
                borderColor: '#b30000',
                label: {
                  borderColor: '#b30000',
                  textAnchor: 'start',
                  style: {
                    color: '#fff',
                    background: '#b30000',
                  },
                  text: `${$t('MAX_SPEED')}: ${result.route.max_speed}km/h`,
                },
              },
              {
                y: result.route.mean_speed,
                borderColor: '#d0aa00',
                label: {
                  borderColor: '#d0aa00',
                  textAnchor: 'start',
                  style: {
                    color: '#fff',
                    background: '#d0aa00',
                  },
                  text: `${$t('AVG_SPEED')}: ${result.route.mean_speed}km/h`,
                },
              },
            ],
          },
          points: [
            {
              x: new Date('01 Dec 2017').getTime(),
              y: result.route.max_speed,
              marker: {
                size: 8,
                fillColor: '#fff',
                strokeColor: 'red',
                radius: 2,
                cssClass: 'apexcharts-custom-class',
              },
              label: {
                borderColor: '#FF4560',
                offsetY: 0,
                style: {
                  color: '#fff',
                  background: '#FF4560',
                },

                text: 'Point Annotation',
              },
            },
          ],
        }"
        :series="speeds"
      ></VueApexCharts>
    </Content>

    <!-- Temperature Chart -->
    <Content
      class="col-span-full mt-8"
      childClass="border-whitee"
      title="temperature_chart"
      :showBreadcrumb="false"
      :showAdd="false"
      v-if="!!result && hasTemperatureData"
    >
      <VueApexCharts
        type="line"
        height="350"
        :options="{
          chartOptions,
          labels: date,
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            enabled: true,
            x: {
              show: true,
            },
            custom: this.customTooltip,
          },
        }"
        :series="temperatures"
      ></VueApexCharts>
    </Content>

    <!-- Fuel Chart -->
    <Content
      class="col-span-full mt-8"
      childClass="border-whitee"
      title="fuel_chart"
      :showBreadcrumb="false"
      :showAdd="false"
      v-if="!!result && hasFuelData"
    >
      <VueApexCharts
        type="line"
        height="350"
        :options="{
          chartOptions,
          labels: date,
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            enabled: true,
            x: {
              show: true,
            },
            custom: this.customFuelTooltip,
          },
        }"
        :series="fuels"
      ></VueApexCharts>
    </Content>
  </Content>
</template>
<script>
import { calculateHour } from '@/mixins/calculateHour'
import LicensePlate from '@/components/cars/LicensePlate'
import { v3overviewDetail } from '@/api/reports'
import OverviewDetailMap from '@/components/reports/history/OverviewDetailMap.vue'
import VueApexCharts from 'vue-apexcharts'
import { licenseChars } from '@/api/global'
import Field from '@/components/elements/field'

export default {
  mixins: [calculateHour],
  components: {
    OverviewDetailMap,
    VueApexCharts,
    LicensePlate,
    Field,
  },
  computed: {
    qualityTitle() {
      const qualityMap = {
        raw: 'خام',
        clean: 'پاک‌سازی شده',
        map_matched: 'تطبیق یافته با نقشه',
      }
      return qualityMap[this.$route.query.quality] ?? this.$route.query.quality
    },
  },
  methods: {
    customTooltip({ series, seriesIndex, dataPointIndex, w }) {
      const value = series[seriesIndex][dataPointIndex]
      const seriesName = w.globals.seriesNames[seriesIndex]
      const xValue = this.formatPersianDate(
        this.result.route.locations[dataPointIndex].datetime
      )

      return (
        '<div style="padding: 10px; background: white; border: 1px solid #e0e0e0; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1)">' +
        '<div style="bold; margin-bottom: 10px;">' +
        xValue +
        '</div>' +
        '<div style="display: flex; align-items: center;">' +
        '<span style="width: 12px; height: 12px; background: ' +
        w.globals.colors[seriesIndex] +
        '; border-radius: 50%; margin-left: 8px;"></span>' +
        '<span style="font-weight: 500;">' +
        seriesName +
        ':</span>' +
        '<span style="margin-right: 5px; font-weight: bold; direction: ltr;">' +
        (value !== null && value !== undefined ? value : 'N/A') +
        (value !== null && value !== undefined ? '°C' : '') +
        '</span>' +
        '</div>' +
        '</div>'
      )
    },
    customFuelTooltip({ series, seriesIndex, dataPointIndex, w }) {
      const value = series[seriesIndex][dataPointIndex]
      const seriesName = w.globals.seriesNames[seriesIndex]
      const xValue = this.formatPersianDate(
        this.result.route.locations[dataPointIndex].datetime
      )

      return (
        '<div style="padding: 10px; background: white; border: 1px solid #e0e0e0; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1)">' +
        '<div style="bold; margin-bottom: 10px;">' +
        xValue +
        '</div>' +
        '<div style="display: flex; align-items: center;">' +
        '<span style="width: 12px; height: 12px; background: ' +
        w.globals.colors[seriesIndex] +
        '; border-radius: 50%; margin-left: 8px;"></span>' +
        '<span style="font-weight: 500;">' +
        seriesName +
        ':</span>' +
        '<span style="margin-right: 5px; font-weight: bold;">' +
        (value !== null && value !== undefined ? value : '') +
        ' ' +
        (value !== null && value !== undefined ? ' ' + this.$i18n.t('L') : '') +
        ' ' +
        '</span>' +
        '</div>' +
        '</div>'
      )
    },
    formatPersianDate(date) {
      const persianDate = new Date(date)
      persianDate.setMinutes(persianDate.getMinutes() + 210)

      return persianDate.toLocaleString('fa-IR')
    },
  },
  data() {
    return {
      routes: null,
      loading: true,
      result: null,
      alphaList: [
        {
          char: '',
          code: '',
        },
      ],
      hasTemperatureData: false,
      hasFuelData: false,
      chartOptions: {
        toolbar: {
          show: false,
          offsetY: -500,
          offsetX: 500,
        },
        chart: {
          height: 350,
          locales: ['fa'],
          defaultLocale: 'fa',
          type: 'line',
          id: 'areachart-2',
          fontFamily: 'Vazir',
        },
        annotations: {
          yaxis: [],
          xaxis: [],
          points: [],
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
        },
        grid: {
          padding: {
            right: 0,
            left: 0,
          },
        },
        title: {
          text: 'Line with Annotations',
          align: 'right',
        },

        xaxis: {
          labels: {
            show: false,
          },
          tooltip: {
            enabled: true,
          },
        },
        tooltip: {
          enabled: true,
          x: {
            show: true,
          },
        },
      },
      speeds: [
        {
          data: [],
          name: this.$i18n.t('SPEED'),
        },
      ],
      temperatures: [
        {
          name: this.$i18n.t('temperature'),
          data: [],
        },
      ],
      fuels: [
        {
          name: this.$i18n.t('fuel'),
          data: [],
        },
      ],
      date: [],
    }
  },
  mounted() {
    licenseChars.api().then((res) => {
      this.alphaList = res
    })

    const actionPayload = [
      this.$route.params.id,
      this.$route.query.start_range,
      this.$route.query.stop_range,
      this.$route.query.quality,
    ]

    v3overviewDetail.api(actionPayload).then((res) => {
      this.loading = false

      if (res) {
        this.result = res

        this.speeds[0].data = res.route.locations.map((i) => i.speed + 'km/h')

        const tempData = res.route.locations.map((i) => {
          if (i.temperature === 250.0) {
            return null
          } else if (i.temperature != null) {
            return i.temperature
          } else {
            return null
          }
        })

        this.hasTemperatureData = tempData.some((temp) => temp !== null)
        this.temperatures[0].data = tempData

        const fuelData = res.route.locations.map((i) => i.fuel)

        this.hasFuelData = fuelData.some((fuel) => fuel !== null)
        this.fuels[0].data = fuelData

        this.date = res.route.locations.map((i) => {
          return this.formatPersianDate(i.datetime)
        })
      } else {
        this.$notify.error({
          title: this.$i18n.t('NO_DATA_FOR_VEHICLE'),
          duration: 5000,
          customClass: 'error_msg',
        })
      }
    })
  },
}
</script>
